import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../api/client';
import { currentAccount, devBypass } from '../auth/msal';

export type PortalRole = 'Owner' | 'Manager' | 'Purchasing' | 'Bookkeeper' | 'McpAdmin';

export interface CurrentUser {
  oid: string;
  name: string;
  roles: PortalRole[];
  viaGlobalAdmin: boolean;
}

/**
 * Who is signed in, and what they may actually do.
 *
 * The display name/username come from the MSAL account (available immediately, no network),
 * while roles come from `GET /admin/v1/me` — the server is the authority there because it
 * applies the Global-Administrator auto-grant that the raw token never spells out as a role.
 */
export function useCurrentUser(): {
  /**
   * The signed-in account's own object id — the same value the server stamps on anything this
   * person records. It is never shown: it is an opaque identifier, and this site puts none on a
   * screen.
   *
   * It is exposed for one job. A two-person control has to be able to tell the reader that THEY
   * are the person who may not press the button; without it the only way to find that out is to
   * press it and be refused, which teaches an owner that a working control is broken.
   */
  oid: string | null;
  name: string;
  username: string | null;
  initials: string;
  roles: PortalRole[];
  /**
   * Whether this is the owner — the only person the figures belong to as of 2026-08-10.
   *
   * Read off the SERVER's answer and not off the token, which is the whole reason this hook exists:
   * a tenant Global Administrator is Owner-equivalent by the `wids` claim, and the raw token never
   * spells that out as a role. The same fact decides both the menu row and the route, so the two
   * cannot come to disagree — and neither of them is the gate. The gate is the mount in
   * `http/router.ts`; this only stops the screen offering a door that answers 403.
   *
   * False while `isLoading`, deliberately: a moment of hiding a row somebody may have is a moment;
   * a moment of showing figures to somebody who may not is the thing being fixed.
   */
  isOwner: boolean;
  viaGlobalAdmin: boolean;
  isLoading: boolean;
} {
  const account = devBypass ? null : currentAccount();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => rawApi<CurrentUser>('GET', '/admin/v1/me'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const name = account?.name || me.data?.name || (devBypass ? 'Developer (bypass)' : 'Signed in');
  const username = account?.username ?? null;

  return {
    // From the server, not the token: it is the same field the API writes onto a record, so a
    // comparison against one is comparing like with like.
    oid: me.data?.oid ?? null,
    name,
    username,
    initials: toInitials(name, username),
    roles: me.data?.roles ?? [],
    isOwner: (me.data?.roles ?? []).includes('Owner'),
    viaGlobalAdmin: me.data?.viaGlobalAdmin ?? false,
    isLoading: me.isLoading,
  };
}

function toInitials(name: string, username: string | null): string {
  const source = name.trim() || username?.trim() || '';
  const words = source.split(/[\s.@_-]+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
